import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { profileData } from '../data/mock';
import {
  ArrowLeft, Terminal, Code2, Cpu, GitBranch,
  Zap, Users, Package, Award, ExternalLink, Database,
} from 'lucide-react';

// ─── Constants ──────────────────────────────────────────────
const C = {
  cyan:   '#06b6d4',
  purple: '#a855f7',
  green:  '#22c55e',
  yellow: '#eab308',
};

const TERMINAL_TEXT = `> whoami
Sandeep Sinha Mahapatra

> cat role.txt
SDE II @ Atlassian · 6+ years

> cat expertise.sh
AI Agents · Microservices · System Design

> _`;

// ─── Hooks ───────────────────────────────────────────────────
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return [ref, inView];
}

// ─── Animated Counter ────────────────────────────────────────
function Counter({ value, suffix = '', inView }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const steps = 60;
    let i = 0;
    const id = setInterval(() => {
      i++;
      setN(Math.round((i / steps) * value));
      if (i >= steps) clearInterval(id);
    }, 30);
    return () => clearInterval(id);
  }, [inView, value]);
  return <>{n.toLocaleString()}{suffix}</>;
}

// ─── Glow Card ───────────────────────────────────────────────
function GlowCard({ children, color = C.cyan, className = '' }) {
  return (
    <div
      className={`rounded-2xl p-px ${className}`}
      style={{ background: `linear-gradient(135deg, ${color}55 0%, transparent 50%, ${color}22 100%)` }}
    >
      <div className="rounded-2xl bg-[#080810] p-6 h-full">
        {children}
      </div>
    </div>
  );
}

// ─── Skill Bar ───────────────────────────────────────────────
function SkillBar({ name, level, color, inView, delay = 0 }) {
  return (
    <div className="mb-5">
      <div className="flex justify-between mb-2">
        <span className="text-sm text-gray-300 font-mono">{name}</span>
        <span className="text-xs font-mono font-bold" style={{ color }}>{level}%</span>
      </div>
      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{
            width: inView ? `${level}%` : '0%',
            background: `linear-gradient(90deg, ${color}66, ${color})`,
            boxShadow: `0 0 12px ${color}99`,
            transition: `width 1.2s cubic-bezier(0.4,0,0.2,1) ${delay}ms`,
          }}
        />
      </div>
    </div>
  );
}

// ─── Main Demo Page ──────────────────────────────────────────
export default function Demo() {
  const [typed, setTyped] = useState('');
  const [statsRef,    statsInView]    = useInView();
  const [skillsRef,   skillsInView]   = useInView();
  const [timelineRef, timelineInView] = useInView();
  const [projectsRef, projectsInView] = useInView();

  // Typewriter effect
  useEffect(() => {
    let i = 0;
    const id = setInterval(() => {
      if (i < TERMINAL_TEXT.length) {
        setTyped(TERMINAL_TEXT.slice(0, i + 1));
        i++;
      } else {
        clearInterval(id);
      }
    }, 22);
    return () => clearInterval(id);
  }, []);

  const stats = [
    { icon: Users,   label: 'Daily Active Users', value: 300000, suffix: '+', color: C.cyan   },
    { icon: Package, label: 'Shipments / Day',     value: 1000000, suffix: '+', color: C.purple },
    { icon: Zap,     label: 'AI Tickets / Month',  value: 110000, suffix: '+', color: C.green  },
    { icon: Award,   label: 'Years Experience',    value: 6,      suffix: '+', color: C.yellow },
  ];

  const skills = [
    { name: 'Kotlin / Java',     level: 92, color: C.cyan,   delay: 0   },
    { name: 'Spring Boot',       level: 90, color: C.cyan,   delay: 100 },
    { name: 'Python',            level: 80, color: C.purple, delay: 200 },
    { name: 'JavaScript',        level: 75, color: C.purple, delay: 300 },
    { name: 'LLM / RAG / AI',    level: 88, color: C.green,  delay: 0   },
    { name: 'GraphQL',           level: 82, color: C.green,  delay: 100 },
    { name: 'System Design',     level: 90, color: C.yellow, delay: 200 },
    { name: 'Kafka / Async',     level: 78, color: C.yellow, delay: 300 },
  ];

  return (
    <div
      className="min-h-screen text-white overflow-x-hidden"
      style={{ background: '#050508', fontFamily: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace" }}
    >
      {/* ── Animated background ── */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {/* Blobs */}
        <div className="absolute top-1/4 -left-40 w-[500px] h-[500px] rounded-full blur-3xl opacity-[0.12] animate-pulse"
          style={{ background: C.cyan, animationDuration: '5s' }} />
        <div className="absolute bottom-1/4 -right-40 w-[500px] h-[500px] rounded-full blur-3xl opacity-[0.10] animate-pulse"
          style={{ background: C.purple, animationDuration: '7s', animationDelay: '2s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] rounded-full blur-3xl opacity-[0.06] animate-pulse"
          style={{ background: C.green, animationDuration: '9s', animationDelay: '4s' }} />
        {/* Dot grid */}
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle, rgba(6,182,212,0.08) 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
        }} />
        {/* Scan line */}
        <div
          className="absolute left-0 right-0 h-px opacity-20"
          style={{
            background: `linear-gradient(90deg, transparent, ${C.cyan}, transparent)`,
            animation: 'scanline 8s linear infinite',
            top: 0,
          }}
        />
      </div>

      <style>{`
        @keyframes scanline {
          0%   { top: -2px; }
          100% { top: 100vh; }
        }
        @keyframes flicker {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        .cursor { animation: flicker 1s step-end infinite; }
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .fade-in { animation: fadeSlideIn 0.6s ease both; }
      `}</style>

      {/* ── Header ── */}
      <header className="relative z-10 flex items-center justify-between px-6 py-4 border-b border-white/5 backdrop-blur-sm">
        <Link
          to="/"
          className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors group"
        >
          <ArrowLeft size={15} className="group-hover:-translate-x-1 transition-transform" />
          <span className="font-mono">back to portfolio</span>
        </Link>
        <div className="flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm">
          <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
          <span className="text-xs font-mono text-gray-400 tracking-widest">DEMO PAGE</span>
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-6 py-20">

        {/* ── HERO ── */}
        <section className="mb-28 fade-in">
          <div className="grid md:grid-cols-2 gap-14 items-center">

            {/* Left: copy */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-cyan-500/25 bg-cyan-500/5 mb-8">
                <Terminal size={11} className="text-cyan-400" />
                <span className="text-xs text-cyan-400 font-mono tracking-wide">interactive demo</span>
              </div>

              <h1 className="text-5xl md:text-6xl font-bold leading-tight mb-5">
                <span className="block text-white">Building</span>
                <span
                  className="block"
                  style={{
                    background: `linear-gradient(135deg, ${C.cyan} 0%, ${C.purple} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  at Scale.
                </span>
              </h1>

              <p className="text-gray-400 text-sm leading-relaxed mb-10 max-w-xs">
                AI-powered systems · Microservices · 6+ years shipping production code
              </p>

              <div className="flex flex-wrap gap-3">
                <a
                  href={`mailto:${profileData.email}`}
                  className="px-5 py-2.5 rounded-xl text-sm font-mono font-bold text-black transition-all hover:scale-105 hover:shadow-lg"
                  style={{
                    background: `linear-gradient(135deg, ${C.cyan}, ${C.purple})`,
                    boxShadow: `0 0 20px ${C.cyan}44`,
                  }}
                >
                  hire me →
                </a>
                <Link
                  to="/contact"
                  className="px-5 py-2.5 rounded-xl text-sm font-mono border border-white/10 text-gray-300 hover:border-cyan-500/40 hover:text-white transition-all"
                >
                  get in touch
                </Link>
              </div>
            </div>

            {/* Right: terminal window */}
            <div
              className="rounded-xl overflow-hidden border border-white/10"
              style={{ boxShadow: `0 0 50px ${C.cyan}18, 0 0 100px ${C.purple}0a` }}
            >
              {/* Title bar */}
              <div className="flex items-center gap-2 px-4 py-3 bg-white/5 border-b border-white/5">
                <span className="w-3 h-3 rounded-full bg-red-500/70" />
                <span className="w-3 h-3 rounded-full bg-yellow-500/70" />
                <span className="w-3 h-3 rounded-full bg-green-500/70" />
                <span className="ml-3 text-xs text-gray-500 font-mono">sandeep@portfolio ~</span>
              </div>
              {/* Body */}
              <div className="p-6 bg-[#0a0a14] min-h-52">
                <pre className="text-sm font-mono leading-relaxed whitespace-pre-wrap text-green-400">
                  {typed}
                  <span className="cursor text-green-300">█</span>
                </pre>
              </div>
            </div>

          </div>
        </section>

        {/* ── STATS ── */}
        <section ref={statsRef} className="mb-28">
          <SectionLabel eyebrow="by the numbers" title="Impact at Scale" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map(({ icon: Icon, label, value, suffix, color }) => (
              <GlowCard key={label} color={color}>
                <Icon size={18} className="mb-4" style={{ color }} />
                <div className="text-3xl font-bold font-mono mb-2" style={{ color }}>
                  <Counter value={value} suffix={suffix} inView={statsInView} />
                </div>
                <p className="text-xs text-gray-500 leading-snug">{label}</p>
              </GlowCard>
            ))}
          </div>
        </section>

        {/* ── SKILLS ── */}
        <section ref={skillsRef} className="mb-28">
          <SectionLabel eyebrow="technical arsenal" title="Skills" />
          <div className="grid md:grid-cols-2 gap-6">
            <GlowCard color={C.cyan} className="w-full">
              <div className="flex items-center gap-2 mb-6">
                <Code2 size={14} style={{ color: C.cyan }} />
                <span className="text-xs font-mono text-gray-400 tracking-wide uppercase">Languages & Frameworks</span>
              </div>
              {skills.slice(0, 4).map(s => <SkillBar key={s.name} {...s} inView={skillsInView} />)}
            </GlowCard>
            <GlowCard color={C.purple} className="w-full">
              <div className="flex items-center gap-2 mb-6">
                <Cpu size={14} style={{ color: C.purple }} />
                <span className="text-xs font-mono text-gray-400 tracking-wide uppercase">Architecture & Systems</span>
              </div>
              {skills.slice(4).map(s => <SkillBar key={s.name} {...s} inView={skillsInView} />)}
            </GlowCard>
          </div>

          {/* Databases + concepts chips */}
          <div className="mt-6 grid md:grid-cols-2 gap-6">
            <GlowCard color={C.green} className="w-full">
              <div className="flex items-center gap-2 mb-4">
                <Database size={14} style={{ color: C.green }} />
                <span className="text-xs font-mono text-gray-400 tracking-wide uppercase">Databases</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {profileData.skills.databases.map(db => (
                  <Chip key={db} label={db} color={C.green} />
                ))}
              </div>
            </GlowCard>
            <GlowCard color={C.yellow} className="w-full">
              <div className="flex items-center gap-2 mb-4">
                <Zap size={14} style={{ color: C.yellow }} />
                <span className="text-xs font-mono text-gray-400 tracking-wide uppercase">Core Concepts</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {profileData.skills.concepts.map(c => (
                  <Chip key={c} label={c} color={C.yellow} />
                ))}
              </div>
            </GlowCard>
          </div>
        </section>

        {/* ── EXPERIENCE TIMELINE ── */}
        <section ref={timelineRef} className="mb-28">
          <SectionLabel eyebrow="career journey" title="Experience" />
          <div className="relative pl-10">
            {/* Vertical glow line */}
            <div
              className="absolute left-3 top-2 bottom-2 w-px"
              style={{ background: `linear-gradient(to bottom, ${C.cyan}, ${C.purple}, transparent)` }}
            />

            <div className="space-y-6">
              {profileData.experience.map((exp, i) => {
                const accent = [C.cyan, C.purple, C.green][i % 3];
                return (
                  <div
                    key={exp.id}
                    style={{
                      opacity: timelineInView ? 1 : 0,
                      transform: timelineInView ? 'none' : 'translateX(-16px)',
                      transition: `all 0.6s ease ${i * 150}ms`,
                    }}
                  >
                    {/* Node dot */}
                    <div
                      className="absolute left-[6px] w-4 h-4 rounded-full border-2 bg-[#050508]"
                      style={{
                        borderColor: accent,
                        boxShadow: `0 0 14px ${accent}`,
                        marginTop: '22px',
                      }}
                    />
                    <GlowCard color={accent}>
                      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                        <div>
                          <p className="text-white font-bold text-base">{exp.company}</p>
                          <p className="text-sm font-mono" style={{ color: accent }}>{exp.role}</p>
                        </div>
                        <span className="text-xs font-mono px-2.5 py-1 rounded-lg border border-white/10 text-gray-500">
                          {exp.duration}
                        </span>
                      </div>
                      <ul className="space-y-2">
                        {exp.achievements.slice(0, 2).map((a, j) => (
                          <li key={j} className="flex gap-3 text-xs text-gray-400 leading-relaxed">
                            <span style={{ color: accent, flexShrink: 0 }}>▸</span>
                            {a}
                          </li>
                        ))}
                      </ul>
                    </GlowCard>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── PROJECTS ── */}
        <section ref={projectsRef} className="mb-28">
          <SectionLabel eyebrow="shipped code" title="Projects" />
          <div className="grid md:grid-cols-2 gap-6">
            {profileData.projects.map((proj, i) => {
              const accent = [C.green, C.yellow][i % 2];
              return (
                <div
                  key={proj.id}
                  style={{
                    opacity: projectsInView ? 1 : 0,
                    transform: projectsInView ? 'none' : 'translateY(20px)',
                    transition: `all 0.6s ease ${i * 150}ms`,
                  }}
                >
                  <GlowCard color={accent} className="h-full">
                    <div className="flex items-start justify-between mb-3">
                      <GitBranch size={16} style={{ color: accent }} />
                      <ExternalLink size={13} className="text-gray-600 hover:text-white cursor-pointer transition-colors" />
                    </div>
                    <h3 className="text-white font-bold mb-2">{proj.name}</h3>
                    <p className="text-xs text-gray-400 leading-relaxed mb-3">{proj.description}</p>
                    <p className="text-xs mb-4 font-mono" style={{ color: accent }}>↗ {proj.impact}</p>
                    <div className="flex flex-wrap gap-2">
                      {proj.technologies.map(t => <Chip key={t} label={t} color={accent} />)}
                    </div>
                  </GlowCard>
                </div>
              );
            })}
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="text-center py-20 rounded-3xl border border-white/5 bg-white/[0.02] relative overflow-hidden">
          {/* background glow */}
          <div
            className="absolute inset-0 opacity-10 blur-3xl"
            style={{ background: `radial-gradient(ellipse at center, ${C.cyan}, ${C.purple}, transparent 70%)` }}
          />
          <div className="relative z-10">
            <p className="text-xs font-mono text-gray-500 uppercase tracking-widest mb-4">open to opportunities</p>
            <h2 className="text-4xl font-bold mb-4 text-white">
              Let's build something{' '}
              <span
                style={{
                  background: `linear-gradient(135deg, ${C.cyan}, ${C.purple})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                remarkable.
              </span>
            </h2>
            <p className="text-gray-400 text-sm mb-8">Available for interesting engineering challenges.</p>
            <a
              href={`mailto:${profileData.email}`}
              className="inline-flex items-center gap-2 px-7 py-3 rounded-xl font-mono font-bold text-black text-sm transition-all hover:scale-105"
              style={{
                background: `linear-gradient(135deg, ${C.cyan}, ${C.purple})`,
                boxShadow: `0 0 30px ${C.cyan}44`,
              }}
            >
              {profileData.email} →
            </a>
          </div>
        </section>

      </main>

      {/* ── Footer ── */}
      <footer className="relative z-10 text-center py-8 border-t border-white/5 mt-8">
        <p className="text-xs font-mono text-gray-600">
          © 2026 {profileData.name} ·{' '}
          <Link to="/" className="hover:text-cyan-400 transition-colors">view full portfolio</Link>
        </p>
      </footer>
    </div>
  );
}

// ─── Small helpers ────────────────────────────────────────────
function SectionLabel({ eyebrow, title }) {
  return (
    <div className="text-center mb-12">
      <span className="text-xs font-mono text-gray-500 uppercase tracking-widest">{eyebrow}</span>
      <h2 className="text-3xl font-bold mt-2 text-white">{title}</h2>
    </div>
  );
}

function Chip({ label, color }) {
  return (
    <span
      className="px-2.5 py-0.5 text-xs font-mono rounded-full border text-gray-400"
      style={{ borderColor: `${color}33`, background: `${color}0d` }}
    >
      {label}
    </span>
  );
}
